import {
  Contact,
  EnumRefundStatus,
  RefundsByOrderIdsQuery,
} from "gql/generated";
import { ReservationInfoCard } from "./ReservationInfoCard";
import { Flex, styled } from "styled-system/jsx";
import { Trans } from "react-i18next";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { formatPrice } from "shared/utils";
import { Link, Text } from "@ttc3k/trekker";

type RefundsProps = {
  orderAmount: number;
  refunds: RefundsByOrderIdsQuery["refundsByOrderIds"];
  contactInfo: Contact | undefined;
};

export const Refunds = ({
  orderAmount,
  refunds,
  contactInfo,
}: RefundsProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.REFUND",
  });

  if (!refunds || refunds.length === 0) {
    return null;
  }

  const totalRefundAmount = refunds.reduce(
    (acc, refund) =>
      acc +
      (refund.status === EnumRefundStatus.Failed ? 0 : (refund.amount ?? 0)),
    0,
  );

  return (
    <Flex flexDir="column" gap="250" w="full">
      {refunds.map(refund => {
        const isFullRefund = refund.amount === orderAmount;
        const refundType = isFullRefund ? t("TYPE_FULL") : t("TYPE_PARTIAL");

        return (
          <ReservationInfoCard
            key={refund._id}
            visual={
              refund.status === EnumRefundStatus.Succeeded
                ? "success"
                : refund.status === EnumRefundStatus.Pending
                  ? "highlight"
                  : "error"
            }
            title={refundType + " " + t(`${refund.status.toUpperCase()}.TITLE`)}
            description={
              <Trans
                i18nKey={`storefront:RESERVATION_DETAILS.REFUND.${refund.status.toUpperCase()}.DESCRIPTION`}
                values={{
                  amount: formatPrice(refund.amount ?? 0),
                  date: format(
                    refund?.createdDateISO ?? new Date(),
                    "MMMM d, yyyy",
                  ),
                  refundType: refundType.toLowerCase(),
                }}
                components={{
                  Bold: <styled.span fontWeight="bold" />,
                  Link: (
                    <Link
                      href={`mailto:${contactInfo?.email}`}
                      color="text.error.dark"
                      textDecoration="underline"
                    />
                  ),
                }}
              />
            }
          />
        );
      })}
      {refunds.length > 1 && totalRefundAmount > 0 && (
        <Text visual="bodyMedium" color="text.light" textAlign="right">
          <Trans
            i18nKey="storefront:RESERVATION_DETAILS.REFUND.TOTAL_REFUNDED"
            values={{
              amount: formatPrice(totalRefundAmount),
            }}
            components={{
              Bold: <styled.span fontWeight="bold" />,
            }}
          />
        </Text>
      )}
    </Flex>
  );
};
